import React from 'react';
import { MapPinIcon, GlobeAltIcon, ExclamationTriangleIcon } from 'components/icons';

/**
 * Renders the teaming-partner shortlist.
 *
 * These are real SAM.gov registrations, not model output — so the UI leans on
 * the verifiable identifiers (UEI, CAGE) and says plainly that a firm is worth
 * calling rather than implying it is qualified. The contractor still qualifies
 * them; overstating that here would send someone to the wrong vendor.
 */
export interface VendorMatchPayload {
  matches_found?: number;
  search_origin?: string | null;
  naics_searched?: string[];
  states_searched?: string[];
  pool_size?: number;
  capability_filtered?: boolean;
  note?: string;
  top_matches?: Array<{
    vendor_id?: string;
    name?: string;
    location?: string;
    distanceMiles?: number;
    naicsCodes?: string[];
    capabilities?: string[];
    cageCode?: string | null;
    entityUrl?: string | null;
    registrationExpiringSoon?: boolean;
    confidence?: string;
    matchReasons?: string[];
  }>;
}

export function isVendorMatchPayload(v: any): v is VendorMatchPayload {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  return Array.isArray(v.top_matches) && (typeof v.matches_found === 'number' || 'search_origin' in v);
}

/** SAM stores urls inconsistently — "www.x.com", "x.com", "https://x.com". */
function href(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const CONFIDENCE_STYLES: Record<string, string> = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-surface-sunk text-content-muted',
  unrated: 'bg-surface-sunk text-content-muted',
};

const VendorMatchResult: React.FC<{ data: VendorMatchPayload }> = ({ data }) => {
  const vendors = data.top_matches ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="text-sm font-semibold text-content">
          {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'} worth calling
        </p>
        {data.search_origin && (
          <span className="text-sm text-content-muted">near {data.search_origin}</span>
        )}
        {typeof data.pool_size === 'number' && (
          <span className="text-xs text-content-subtle">
            · from {data.pool_size} registered nearby
          </span>
        )}
      </div>

      {data.note && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
          <p className="text-xs text-amber-800">{data.note}</p>
        </div>
      )}

      <p className="text-xs text-content-muted">
        Registered in SAM.gov under NAICS {(data.naics_searched ?? []).join(', ')}. Verify each firm
        can perform this specific scope before relying on them — proximity and trade are a starting
        point, not a qualification.
      </p>

      <ul className="space-y-3">
        {vendors.map((v, i) => {
          const link = href(v.entityUrl);
          return (
            <li key={v.vendor_id || i} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-content">{v.name}</p>
                  <p className="flex items-center gap-1 text-xs text-content-muted mt-0.5">
                    <MapPinIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    {v.location}
                    {typeof v.distanceMiles === 'number' && (
                      <span className="font-medium text-content"> · {v.distanceMiles} mi</span>
                    )}
                  </p>
                </div>
                {v.confidence && v.confidence !== 'unrated' && (
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${
                      CONFIDENCE_STYLES[v.confidence] || CONFIDENCE_STYLES.unrated
                    }`}
                  >
                    {v.confidence} fit
                  </span>
                )}
              </div>

              {/* Identifiers are the point: a contractor can look these up and
                  confirm the firm exists before picking up the phone. */}
              <p className="text-xs text-content-subtle mt-2 font-mono">
                UEI {v.vendor_id}
                {v.cageCode ? ` · CAGE ${v.cageCode}` : ''}
                {v.naicsCodes?.length ? ` · NAICS ${v.naicsCodes.join(', ')}` : ''}
              </p>

              {!!v.capabilities?.length && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {v.capabilities.map((c) => (
                    <span
                      key={c}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 font-medium"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {!!v.matchReasons?.length && (
                <ul className="mt-2 space-y-0.5">
                  {v.matchReasons.map((r, ri) => (
                    <li key={ri} className="text-xs text-content-muted">
                      {r}
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-3 mt-2">
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <GlobeAltIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Website
                  </a>
                )}
                {v.registrationExpiringSoon && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    SAM registration expires within 90 days
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default VendorMatchResult;
